import { AUTH_BASE_URL } from '../config/api';

// Get all context spaces
export const getContextSpaces = async () => {
  const response = await fetch(`${AUTH_BASE_URL}/api/context-spaces`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch context spaces');
  }

  return response.json();
};

// Get a specific context space with all resources and conversations
export const getContextSpace = async (contextSpaceId) => {
  const response = await fetch(`${AUTH_BASE_URL}/api/context-spaces/${contextSpaceId}`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch context space');
  }

  return response.json();
};

// Create a new context space
export const createContextSpace = async (contextSpaceData) => {
  const response = await fetch(`${AUTH_BASE_URL}/api/context-spaces`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(contextSpaceData),
  });

  if (!response.ok) {
    throw new Error('Failed to create context space');
  }

  return response.json();
};

// Update a context space
export const updateContextSpace = async (contextSpaceId, updateData) => {
  const response = await fetch(`${AUTH_BASE_URL}/api/context-spaces/${contextSpaceId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(updateData),
  });

  if (!response.ok) {
    throw new Error('Failed to update context space');
  }

  return response.json();
};

// Delete a context space
export const deleteContextSpace = async (contextSpaceId) => {
  const response = await fetch(`${AUTH_BASE_URL}/api/context-spaces/${contextSpaceId}`, {
    method: 'DELETE',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to delete context space');
  }

  return response.json();
};

// Add a conversation to a context space
export const addConversationToContextSpace = async (contextSpaceId, conversationId) => {
  const response = await fetch(`${AUTH_BASE_URL}/api/context-spaces/${contextSpaceId}/conversations`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ conversationId }),
  });

  if (!response.ok) {
    throw new Error('Failed to add conversation to context space');
  }

  return response.json();
};

// Remove a conversation from a context space
export const removeConversationFromContextSpace = async (contextSpaceId, conversationId) => {
  const response = await fetch(`${AUTH_BASE_URL}/api/context-spaces/${contextSpaceId}/conversations/${conversationId}`, {
    method: 'DELETE',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to remove conversation from context space');
  }

  return response.json();
};

// Add a resource to a context space
export const addResourceToContextSpace = async (contextSpaceId, resourceData) => {
  const response = await fetch(`${AUTH_BASE_URL}/api/context-spaces/${contextSpaceId}/resources`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(resourceData),
  });

  if (!response.ok) {
    throw new Error('Failed to add resource to context space');
  }

  return response.json();
};

// Remove a resource from a context space
export const removeResourceFromContextSpace = async (contextSpaceId, resourceId) => {
  const response = await fetch(`${AUTH_BASE_URL}/api/context-spaces/${contextSpaceId}/resources/${resourceId}`, {
    method: 'DELETE',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to remove resource from context space');
  }

  return response.json();
};

// Get aggregated context for a context space
export const getContextSpaceContext = async (contextSpaceId) => {
  const response = await fetch(`${AUTH_BASE_URL}/api/context-spaces/${contextSpaceId}/context`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to get context space context');
  }

  return response.json();
};

// Helper functions for common resource types

// Add a URL resource
export const addUrlResource = async (contextSpaceId, { url, title, description, tags = [] }) => {
  return addResourceToContextSpace(contextSpaceId, {
    type: 'URL',
    title,
    description,
    url,
    tags
  });
};

// Add a file resource
export const addFileResource = async (contextSpaceId, { fileId, title, description, tags = [] }) => {
  return addResourceToContextSpace(contextSpaceId, {
    type: 'FILE',
    title,
    description,
    fileId,
    tags
  });
};

// Add a note resource
export const addNoteResource = async (contextSpaceId, { title, description, content, tags = [] }) => {
  return addResourceToContextSpace(contextSpaceId, {
    type: 'NOTE',
    title,
    description,
    content,
    tags
  });
};

// Add a text resource
export const addTextResource = async (contextSpaceId, { title, description, content, tags = [] }) => {
  return addResourceToContextSpace(contextSpaceId, {
    type: 'TEXT',
    title,
    description,
    content,
    tags
  });
};

// Predefined icons for context spaces
export const CONTEXT_SPACE_ICONS = [
  '📁', '📂', '📊', '🗂️', '📋', '📝', '🗃️', '🏷️',
  '💼', '🎯', '📈', '🔬', '📘', '📗', '📙', '📕',
  '🧠', '💡', '🔍', '⚡', '🎨', '🏗️', '🎪', '🎭'
];

// Predefined colors for context spaces
export const CONTEXT_SPACE_COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Emerald
  '#8B5CF6', // Purple
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#06B6D4', // Cyan
  '#84CC16', // Lime
  '#F97316', // Orange
  '#EC4899', // Pink
  '#6B7280', // Gray
  '#14B8A6', // Teal
  '#8B5A2B'  // Brown
];